import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { PlateMindLogo } from "@/components/PlateMindLogo";
import { toast } from "sonner";
import { BottomNav } from "@/components/BottomNav";
import {
  Camera, Upload, X, Star, Send, ArrowLeft,
  Clock, MessageSquare, Home, Image as ImageIcon,
  ChevronRight, Loader2
} from "lucide-react";

const MealJournal = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [caption, setCaption] = useState("");
  const [mealType, setMealType] = useState("lunch");
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const res = await fetch(`${API}/meal-photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPhotos(data.photos || []);
      }
    } catch (err) {
      console.error("Error fetching meal photos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !caption.trim()) {
      toast.error("Please add a photo and caption");
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result;
        const res = await fetch(`${API}/meal-photos`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            image_base64: base64,
            caption: caption.trim(),
            meal_type: mealType
          })
        });
        if (res.ok) {
          toast.success("Meal photo uploaded!");
          setShowUpload(false);
          setCaption("");
          setMealType("lunch");
          setPreview(null);
          setSelectedFile(null);
          fetchPhotos();
        } else {
          const err = await res.json();
          toast.error(err.detail || "Upload failed");
        }
        setUploading(false);
      };
      reader.readAsDataURL(selectedFile);
    } catch (err) {
      toast.error("Upload failed");
      setUploading(false);
    }
  };

  const mealTypeLabels = {
    breakfast: "Breakfast",
    lunch: "Lunch",
    dinner: "Dinner",
    snack: "Snack"
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star
            key={s}
            className={`w-3.5 h-3.5 ${s <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/30 to-white">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-orange-100/50 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </Link>
            <div>
              <h1 className="text-lg font-bold text-secondary flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                Meal Journal
              </h1>
              <p className="text-xs text-muted-foreground">Share your meals for physician review</p>
            </div>
          </div>
          <Button
            onClick={() => setShowUpload(true)}
            className="rounded-full bg-primary hover:bg-primary/90"
            data-testid="upload-meal-photo-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Add Meal
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Upload Modal */}
        {showUpload && (
          <Card className="p-5 rounded-2xl border-primary/20 bg-white space-y-4" data-testid="upload-meal-form">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-secondary">Upload Meal Photo</h3>
              <button onClick={() => { setShowUpload(false); setPreview(null); setSelectedFile(null); }}>
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Photo Preview / Upload Area */}
            {preview ? (
              <div className="relative rounded-xl overflow-hidden">
                <img src={preview} alt="Preview" className="w-full h-64 object-cover rounded-xl" />
                <button
                  onClick={() => { setPreview(null); setSelectedFile(null); }}
                  className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-orange-200 rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-orange-50/30 transition-colors"
                data-testid="photo-upload-area"
              >
                <Camera className="w-10 h-10 text-primary/40 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Tap to take or choose a photo</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Max 5MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="photo-file-input"
            />

            {/* Meal Type */}
            <div className="flex gap-2">
              {Object.entries(mealTypeLabels).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setMealType(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    mealType === key
                      ? "bg-primary text-white"
                      : "bg-orange-50 text-muted-foreground hover:bg-orange-100"
                  }`}
                  data-testid={`meal-type-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Caption */}
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="What are you eating? e.g. Jollof rice with grilled chicken"
              className="w-full border border-orange-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
              rows={2}
              data-testid="meal-caption-input"
            />

            {/* Submit */}
            <Button
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !caption.trim()}
              className="w-full rounded-xl bg-primary hover:bg-primary/90"
              data-testid="submit-meal-photo-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Share with Physician
                </>
              )}
            </Button>
          </Card>
        )}

        {/* Photos List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-white rounded-2xl h-72 border border-orange-100/30" />
            ))}
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-16">
            <ImageIcon className="w-16 h-16 text-orange-200 mx-auto mb-4" />
            <h3 className="font-bold text-secondary mb-1">No meal photos yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Upload photos of your meals for your physician to review</p>
            <Button
              onClick={() => setShowUpload(true)}
              className="rounded-full bg-primary hover:bg-primary/90"
            >
              <Camera className="w-4 h-4 mr-2" />
              Upload First Meal
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {photos.map((photo) => (
              <Card
                key={photo.photo_id}
                className="rounded-2xl border-orange-100/50 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedPhoto(selectedPhoto?.photo_id === photo.photo_id ? null : photo)}
                data-testid={`meal-photo-${photo.photo_id}`}
              >
                <img
                  src={photo.image_url}
                  alt={photo.caption}
                  className="w-full h-52 object-cover"
                />
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="px-2 py-0.5 rounded-full bg-orange-50 text-primary text-xs font-medium">
                      {mealTypeLabels[photo.meal_type] || photo.meal_type}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(photo.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-secondary">{photo.caption}</p>

                  {/* Physician Feedback */}
                  {photo.feedback ? (
                    <div className="mt-2 p-3 bg-blue-50/50 rounded-xl border border-blue-100/50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-blue-700 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {photo.feedback.physician_name || "Your Physician"}
                        </span>
                        {photo.feedback.rating && renderStars(photo.feedback.rating)}
                      </div>
                      {photo.feedback.comment && (
                        <p className="text-xs text-blue-800/80">{photo.feedback.comment}</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground/60">
                      <Clock className="w-3 h-3" />
                      Awaiting physician review
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Bottom Nav */}
      <BottomNav />
    </div>
  );
};

export default MealJournal;
